// src/scenes/game-components/BackgroundManager.js
// 🌅 Enhanced BackgroundManager - UPDATED: Detailed thematic backgrounds for each level
// Level 1: Shopping theme with varied shops and planes
// Level 2: Outdoor ceremony pavilion with decorations  
// Level 3: Party with fire bowls and Death Star

import { gameState } from '../../game/state.js';
import { GAME_CONFIG } from '../../game/config.js';

export default class BackgroundManager {
  constructor(scene) {
    this.scene = scene;
    this.backgroundElements = [];
    this.animatedElements = [];
    this.lastSpawnX = 0; // NEW: Track spawn position
    this.spawnDistance = 600; // NEW: Distance between spawns
    this.lastBigBuildingX = -1000;
    this.minBuildingDistance = 400; 
  }

  startContinuousSpawning() {
    this.scene.time.addEvent({
      delay: 300, // FIXED: Was 500ms, now faster spawning
      callback: () => {
        const player = this.scene.getPlayer();
        if (!player) return;
        
        const { width } = this.scene.scale;
        
        // FIXED: Spawn further ahead and more frequently
        while (this.lastSpawnX < player.x + (width * 3)) { // Was width * 2
          this.spawnBackgroundElement(this.lastSpawnX + this.spawnDistance);
          this.lastSpawnX += this.spawnDistance;
        }
      },
      loop: true
    });
  }

  spawnBackgroundElement(x) {
    const { height } = this.scene.scale;
    
    switch (gameState.level) {
      case 1:
        // FIXED: Improved building spawning logic
        const distanceFromLastBuilding = x - this.lastBigBuildingX;
        
        if (distanceFromLastBuilding > this.minBuildingDistance && Math.random() > 0.6) {
          // Spawn big building with spacing
          this.createVariedShop(x, height, 'big', Math.floor(x / 100));
          this.lastBigBuildingX = x;
        } else if (Math.random() > 0.3) {
          // Spawn small shop more frequently
          this.createVariedShop(x, height, 'small', Math.floor(x / 100));
        }
        break;
      case 2:
        if (Math.random() > 0.3) {
          this.createCeremonyDecoration(x, height, Math.floor(x / 100));
        }
        break;
      case 3:
        if (Math.random() > 0.2) {
          this.createPartyDecoration(x, height, Math.floor(x / 100));
        }
        break;
    }
  }


  createLevelBackground() {
    const { width, height } = this.scene.scale;
    
    // Create sky gradient based on level
    this.createSkyGradient();
    
    // Add level-specific background elements
    switch (gameState.level) {
      case 1:
        this.createShoppingBackground();
        this.createShopDecorations();
        break;
      case 2:
        this.createCeremonyBackground();
        this.createCeremonyDecorations();
        break;
      case 3:
        this.createPartyBackground();
        this.createPartyDecorations();
        break;
    }

    this.startContinuousSpawning();
    this.startGroundTextureSpawning(); // ADDED: Start ground texture spawning
    
    /* console.log(`🌅 Enhanced Level ${gameState.level} background created`); */
  }

  createSkyGradient() {
    const { width, height } = this.scene.scale;
    
    let topColor, bottomColor;
    
    switch (gameState.level) {
      case 1: // Shopping - bright day
        topColor = 0x87CEEB; // Sky blue
        bottomColor = 0xE0F6FF; // Light blue
        break;
      case 2: // Ceremony - romantic sunset
        topColor = 0xFF6B6B; // Soft pink
        bottomColor = 0xFFE4B5; // Peach
        break;
      case 3: // Party - night sky
        topColor = 0x191970; // Midnight blue
        bottomColor = 0x4B0082; // Indigo
        break;
    }
    
    const skyGradient = this.scene.add.graphics();
    skyGradient.fillGradientStyle(topColor, topColor, bottomColor, bottomColor, 1);
    skyGradient.fillRect(0, 0, width * 10, height);
    skyGradient.setScrollFactor(0.1); // FIXED: Was 0.1
    skyGradient.setDepth(-1000); // ADDED: Explicit depth
    
    this.backgroundElements.push(skyGradient);
  }


  // === LEVEL 1: SHOPPING THEME ===
  
  createShoppingBackground() {
    const { width, height } = this.scene.scale;
    
    // Enhanced clouds with variety
    this.createVariedClouds();
    
    // Add planes for shopping theme
    this.createShoppingPlanes();
    
    // Add birds
    this.createBirds();
    
    // Add shopping balloons
    this.createShoppingBalloons();
  }

  createVariedClouds() {
    const { width, height } = this.scene.scale;
    
    for (let i = 0; i < 8; i++) {
      const cloudSize = 0.8 + Math.random() * 1.5;
      const cloud = this.scene.add.image(
        (i * width * 0.8) + Math.random() * 200,
        height * 0.1 + Math.random() * (height * 0.3),
        'cloud'
      );
      
      cloud.setScale(cloudSize);
      cloud.setScrollFactor(0.3); // FIXED: Was 0.2
      cloud.setDepth(-900 + i); // ADDED: Explicit depth
      cloud.setAlpha(0.7 + Math.random() * 0.3);
      
      // Slow cloud drift
      this.scene.tweens.add({
        targets: cloud,
        x: cloud.x + 100,
        duration: 15000 + Math.random() * 10000,
        repeat: -1,
        yoyo: true,
        ease: 'Sine.easeInOut'
      });
      
      this.backgroundElements.push(cloud);
    }
  }

  createShoppingPlanes() {
    const { width, height } = this.scene.scale;
    
    // Create plane sprite
    this.createPlaneSprite();
    
    // Add planes with banners - BIGGER AND MORE VISIBLE
    for (let i = 0; i < 2; i++) {
      const plane = this.scene.add.image(
        -150 + (i * width * 1.5), // Start further left for bigger banner
        height * 0.12 + (i * 40),  // Slightly higher and more spacing
        'plane'
      );
      
      plane.setScale(3); // BIGGER: Was 3, now 5
      plane.setScrollFactor(0.3);
      
      // Plane movement
      this.scene.tweens.add({
        targets: plane,
        x: width * 2.5, // Go further right
        duration: 25000, // Slower for better readability
        repeat: -1,
        delay: i * 12000, // More delay between planes
        ease: 'Linear'
      });
      
      // MUCH BIGGER BANNER behind plane
      const bannerWidth = 200; // Was 120, now 200
      const bannerHeight = 35;  // Was 20, now 35
      
      const banner = this.scene.add.rectangle(
        plane.x - 120, // Further behind the bigger plane
        plane.y + 15,  // Slightly lower
        bannerWidth,
        bannerHeight,
        0xFFFFFF
      );
      banner.setScrollFactor(0.3);
      
      // Banner border for better visibility
      const bannerBorder = this.scene.add.rectangle(
        plane.x - 120,
        plane.y + 15,
        bannerWidth,
        bannerHeight,
        0x000000,
        0 // Transparent fill
      );
      bannerBorder.setStrokeStyle(3, 0xFF1493); // Pink border
      bannerBorder.setScrollFactor(0.3);
      
      // BIGGER BANNER TEXT
      const bannerText = this.scene.add.text(
        banner.x,
        banner.y,
        i === 0 ? 'WEDDING 06.06.2025!' : 'PABLO & BETTINA', // Fixed typo: PABLO not PABLE
        {
          fontSize: '14px', // Was 8px, now 14px
          fontFamily: 'Arial, sans-serif', // More readable retro font
          color: '#FF1493',
          fontWeight: 'bold',
          stroke: '#000000', // Black outline for better visibility
          strokeThickness: 2
        }
      ).setOrigin(0.5);
      bannerText.setScrollFactor(0.3);
      
      // Make banner and border follow plane
      this.scene.tweens.add({
        targets: [banner, bannerBorder, bannerText],
        x: width * 2.5 - 120, // Match plane destination
        duration: 25000,
        repeat: -1,
        delay: i * 12000,
        ease: 'Linear'
      });
      
      // Add gentle banner wave effect
      this.scene.tweens.add({
        targets: [banner, bannerBorder, bannerText],
        y: banner.y + 5,
        duration: 2000,
        repeat: -1,
        yoyo: true,
        ease: 'Sine.easeInOut'
      });
      
      this.backgroundElements.push(plane, banner, bannerBorder, bannerText);
    }
  }

  createShoppingBalloons() {
    const { width, height } = this.scene.scale;
    
    // Create balloon sprite
    this.createBalloonSprite();
    
    for (let i = 0; i < 6; i++) {
      const balloon = this.scene.add.image(
        Math.random() * width * 2,
        height * 0.3 + Math.random() * (height * 0.4),
        'balloon'
      );
      
      balloon.setScale(2 + Math.random());
      balloon.setScrollFactor(0.4);
      balloon.setTint(Phaser.Utils.Array.GetRandom([0xFF69B4, 0x4ECDC4, 0xFFD700, 0xFF6B6B]));
      
      // Balloon floating
      this.scene.tweens.add({
        targets: balloon,
        y: balloon.y - 20,
        duration: 3000 + Math.random() * 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
      
      this.backgroundElements.push(balloon);
    }
  }

  createShopDecorations() {
    const { width, height } = this.scene.scale;
    
    // Create varied shop sprites
    this.createShopSprites();
    
    // FIXED: Better initial shop placement with spacing
    let lastBigShopX = -1000;
    const minSpacing = 350;
    
    for (let i = 0; i < 8; i++) { // Reduced from 12 to 8 for better spacing
      const x = width + (i * 400) + Math.random() * 100; // Increased spacing
      
      const distanceFromLast = x - lastBigShopX;
      let shopType;
      
      if (distanceFromLast > minSpacing && Math.random() > 0.7) {
        shopType = 'big';
        lastBigShopX = x;
      } else {
        shopType = 'small';
      }
      
      this.createVariedShop(x, height, shopType, i);
    }
    
   /*  console.log('🏪 Shop decorations created with proper spacing'); */
  }

  createVariedShop(x, height, type, index) {
    const groundLevel = height - 30;
    
    if (type === 'big') {
      const buildingHeight = 200 + Math.random() * 80;
      const buildingWidth = 150 + Math.random() * 50;
      
      const building = this.scene.add.rectangle(x, groundLevel - buildingHeight/2, buildingWidth, buildingHeight, 0xD2B48C);
      building.setScrollFactor(0.4); // FIXED: Was 0.3
      building.setDepth(-200); // ADDED: Explicit depth
      building.setStrokeStyle(6, 0x8B4513);
      
      const signBg = this.scene.add.rectangle(x, groundLevel - buildingHeight + 25, buildingWidth - 20, 50, 0xFF1493);
      signBg.setScrollFactor(0.4); // FIXED: Was 0.3
      signBg.setDepth(-199); // ADDED: Explicit depth
      
      const shopNames = ['BRIDAL MEGA STORE', 'WEDDING SUPERMARKET', 'FORMAL WEAR OUTLET', 'NICE SHOES'];
      const signText = this.scene.add.text(x, groundLevel - buildingHeight + 25, Phaser.Utils.Array.GetRandom(shopNames), {
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif',
        color: '#FFFFFF',
        fontWeight: 'bold',
        align: 'center'
      }).setOrigin(0.5);
      signText.setScrollFactor(0.4); // FIXED: Was 0.3
      signText.setDepth(-198); // ADDED: Explicit depth
      
      // Windows - all at same depth layer
      const floors = Math.floor(buildingHeight / 45);
      for (let floor = 0; floor < floors; floor++) {
        for (let window = 0; window < 4; window++) {
          const windowSprite = this.scene.add.rectangle(
            x - buildingWidth/3 + (window * buildingWidth/5),
            groundLevel - buildingHeight + 60 + (floor * 45),
            buildingWidth/6,
            30,
            0x87CEEB
          );
          windowSprite.setScrollFactor(0.4); // FIXED: Was 0.3
          windowSprite.setDepth(-197); // ADDED: Explicit depth
          windowSprite.setStrokeStyle(3, 0x2F4F4F);
          this.backgroundElements.push(windowSprite);
        }
      }
      
      const entrance = this.scene.add.rectangle(x, groundLevel - 25, buildingWidth/3, 50, 0x654321);
      entrance.setScrollFactor(0.4); // FIXED: Was 0.3
      entrance.setDepth(-196); // ADDED: Explicit depth
      
      this.backgroundElements.push(building, signBg, signText, entrance);
      
    } else {
      // Small shops
      const shopHeight = 120 + Math.random() * 30;
      const shopWidth = 100 + Math.random() * 20;
      
      const shop = this.scene.add.rectangle(x, groundLevel - shopHeight/2, shopWidth, shopHeight, 0xF5DEB3);
      shop.setScrollFactor(0.5); // FIXED: Was 0.4
      shop.setDepth(-150); // ADDED: Explicit depth
      shop.setStrokeStyle(4, 0x8B4513);
      
      const awning = this.scene.add.rectangle(x, groundLevel - shopHeight + 20, shopWidth + 20, 30, 0xFF69B4);
      awning.setScrollFactor(0.5); // FIXED: Was 0.4
      awning.setDepth(-149); // ADDED: Explicit depth
      
      const shopNames = ['Anita\'s Bridal', 'Nice Rings', 'Rose Petals Boutique', 'Love 4 Ever'];
      const shopSign = this.scene.add.text(x, groundLevel - shopHeight + 5, Phaser.Utils.Array.GetRandom(shopNames), {
        fontSize: '12px',
        fontFamily: 'Arial, sans-serif',
        color: '#FFFFFF',
        fontWeight: 'bold'
      }).setOrigin(0.5);
      shopSign.setScrollFactor(0.5); // FIXED: Was 0.4
      shopSign.setDepth(-148); // ADDED: Explicit depth
      
      const window = this.scene.add.rectangle(x, groundLevel - shopHeight/2, shopWidth * 0.6, shopHeight * 0.4, 0xF0F8FF);
      window.setScrollFactor(0.5); // FIXED: Was 0.4
      window.setDepth(-147); // ADDED: Explicit depth
      window.setStrokeStyle(4, 0x8B4513);
      
      const mannequin = this.scene.add.rectangle(x, groundLevel - shopHeight/2, 12, 30, 0xFFDBB5);
      mannequin.setScrollFactor(0.5); // FIXED: Was 0.4
      mannequin.setDepth(-146); // ADDED: Explicit depth
      
      const dress = this.scene.add.rectangle(x, groundLevel - shopHeight/2 + 5, 20, 25, 0xFFFFFF);
      dress.setScrollFactor(0.5); // FIXED: Was 0.4
      dress.setDepth(-145); // ADDED: Explicit depth
      
      this.backgroundElements.push(shop, awning, shopSign, window, mannequin, dress);
    }
  }

  // === LEVEL 2: CEREMONY THEME ===
  
  createCeremonyBackground() {
    const { width, height } = this.scene.scale;
    
    // Romantic sunset clouds
    this.createSunsetClouds();
    
    // Flying doves
    this.createDoves();
    
    // Floating rose petals
    this.createFloatingPetals();
    
    // Romantic lighting
    this.createRomanticLighting();
  }

  createSunsetClouds() {
    const { width, height } = this.scene.scale;
    
    for (let i = 0; i < 6; i++) {
      const cloud = this.scene.add.image(
        (i * width * 0.7) + Math.random() * 150,
        height * 0.08 + Math.random() * (height * 0.25),
        'cloud'
      );
      
      cloud.setScale(1.2 + Math.random() * 0.8);
      cloud.setScrollFactor(0.2);
      cloud.setTint(0xFFB6C1); // Pink tinted clouds
      cloud.setAlpha(0.8);
      
      // Gentle cloud movement
      this.scene.tweens.add({
        targets: cloud,
        x: cloud.x + 50,
        duration: 20000 + Math.random() * 10000,
        repeat: -1,
        yoyo: true,
        ease: 'Sine.easeInOut'
      });
      
      this.backgroundElements.push(cloud);
    }
  }

  createDoves() {
    const { width, height } = this.scene.scale;
    
    // Create dove sprite
    this.createDoveSprite();
    
    for (let i = 0; i < 4; i++) {
      const dove = this.scene.add.image(
        Math.random() * width * 1.5,
        height * 0.2 + Math.random() * (height * 0.3),
        'dove'
      );
      
      dove.setScale(2);
      dove.setScrollFactor(0.3);
      
      // Dove flying pattern
      this.scene.tweens.add({
        targets: dove,
        x: dove.x + 200,
        y: dove.y - 30,
        duration: 8000 + Math.random() * 4000,
        repeat: -1,
        yoyo: true,
        ease: 'Sine.easeInOut'
      });
      
      // Wing flapping
      this.scene.tweens.add({
        targets: dove,
        scaleY: 1.8,
        duration: 300,
        repeat: -1,
        yoyo: true
      });
      
      this.backgroundElements.push(dove);
    }
  }

  createFloatingPetals() {
    const { width, height } = this.scene.scale;
    
    // Create petal sprite
    this.createPetalSprite();
    
    for (let i = 0; i < 15; i++) {
      const petal = this.scene.add.image(
        Math.random() * width * 2,
        Math.random() * height,
        'petal'
      );
      
      petal.setScale(1.5 + Math.random());
      petal.setScrollFactor(0.5);
      petal.setTint(Phaser.Utils.Array.GetRandom([0xFF69B4, 0xFFB6C1, 0xFFFFFF, 0xFFC0CB]));
      petal.setAlpha(0.7);
      
      // Petal floating
      this.scene.tweens.add({
        targets: petal,
        y: petal.y + height,
        x: petal.x + (Math.random() - 0.5) * 100,
        rotation: Math.PI * 2,
        duration: 12000 + Math.random() * 8000,
        repeat: -1,
        ease: 'Linear'
      });
      
      this.backgroundElements.push(petal);
    }
  }

  createRomanticLighting() {
    const { width, height } = this.scene.scale;
    
    // Soft golden glow
    const glow = this.scene.add.graphics();
    glow.fillGradientStyle(0xFFD700, 0xFFD700, 0xFFD700, 0xFFD700, 0.1);
    glow.fillRect(0, 0, width * 10, height);
    glow.setScrollFactor(0);
    glow.setBlendMode(Phaser.BlendModes.SOFT_LIGHT);
    
    this.backgroundElements.push(glow);
  }

  createCeremonyDecorations() {
    const { width, height } = this.scene.scale;
    
    // Create pavilion sprites
    this.createPavilionSprites();
    
    // Main ceremony pavilion
    const pavilionX = width * 1.5;
    this.createOutdoorPavilion(pavilionX, height);
    
    // Additional ceremony decorations
    for (let i = 0; i < 8; i++) {
      const x = width + (i * 400) + 200;
      this.createCeremonyDecoration(x, height, i);
    }
  }

  createOutdoorPavilion(x, height) {
    const groundLevel = height - 30;
    
    // Pavilion structure (white elegant tent)
    const pavilion = this.scene.add.image(x, groundLevel - 80, 'pavilion');
    pavilion.setScale(6, 4);
    pavilion.setScrollFactor(0.2); // FIXED: Was 0.6
    pavilion.setDepth(-300); // ADDED: Explicit depth
    
    // Decorative draping
    const draping = this.scene.add.image(x, groundLevel - 100, 'draping');
    draping.setScale(6, 2);
    draping.setScrollFactor(0.2); // FIXED: Was 0.6
    draping.setDepth(-299); // ADDED: Explicit depth
    draping.setTint(0xF8F8FF);
    
    // Floral arrangements at pavilion
    for (let i = -2; i <= 2; i++) {
      const flowerStand = this.scene.add.image(
        x + (i * 60),
        groundLevel - 20,
        'flower-stand'
      );
      flowerStand.setScale(2.5);
      flowerStand.setScrollFactor(0.2); // FIXED: Was 0.6
      flowerStand.setDepth(-298); // ADDED: Explicit depth
      
      this.backgroundElements.push(flowerStand);
    }
    
    // Wedding arch
    const arch = this.scene.add.image(x, groundLevel - 60, 'wedding-arch');
    arch.setScale(5, 3);
    arch.setScrollFactor(0.2); // FIXED: Was 0.6
    arch.setDepth(-297); // ADDED: Explicit depth
    
    // Hanging lanterns
    for (let i = -1; i <= 1; i++) {
      const lantern = this.scene.add.image(
        x + (i * 80),
        groundLevel - 120,
        'lantern'
      );
      lantern.setScale(2);
      lantern.setScrollFactor(0.2); // FIXED: Was 0.6
      lantern.setDepth(-296); // ADDED: Explicit depth
      
      // Gentle lantern sway
      this.scene.tweens.add({
        targets: lantern,
        rotation: 0.1,
        duration: 2000,
        repeat: -1,
        yoyo: true,
        ease: 'Sine.easeInOut'
      });
      
      this.backgroundElements.push(lantern);
    }
    
    this.backgroundElements.push(pavilion, draping, arch);
  }


  createCeremonyDecoration(x, height, index) {
    const groundLevel = height - 30;
    const decorations = ['flower-stand', 'ceremonial-candle', 'guest-seating', 'flower-arch'];
    const decoration = Phaser.Utils.Array.GetRandom(decorations);
    
    switch (decoration) {
      case 'flower-stand':
        const flowerStand = this.scene.add.image(x, groundLevel - 25, 'flower-stand');
        flowerStand.setScale(2);
        flowerStand.setScrollFactor(0.4); // FIXED: Was 0.7, now behind player
        flowerStand.setDepth(-100); // ADDED: Explicit depth behind player
        this.backgroundElements.push(flowerStand);
        break;
        
      case 'ceremonial-candle':
        const candle = this.scene.add.image(x, groundLevel - 30, 'ceremonial-candle');
        candle.setScale(2.5);
        candle.setScrollFactor(0.4); // FIXED: Was 0.7
        candle.setDepth(-100); // ADDED: Behind player
        
        // Flickering candle light
        this.scene.tweens.add({
          targets: candle,
          alpha: 0.8,
          duration: 1000 + Math.random() * 500,
          repeat: -1,
          yoyo: true
        });
        
        this.backgroundElements.push(candle);
        break;
        
      case 'guest-seating':
        // White chairs in rows
        for (let row = 0; row < 3; row++) {
          for (let seat = 0; seat < 4; seat++) {
            const chair = this.scene.add.image(
              x + (seat * 15) - 30,
              groundLevel - 15 - (row * 20),
              'wedding-chair'
            );
            chair.setScale(1.5);
            chair.setScrollFactor(0.4); // FIXED: Was 0.8
            chair.setDepth(-100); // ADDED: Behind player
            this.backgroundElements.push(chair);
          }
        }
        break;
        
      case 'flower-arch':
        const flowerArch = this.scene.add.image(x, groundLevel - 50, 'flower-arch');
        flowerArch.setScale(3, 2.5);
        flowerArch.setScrollFactor(0.4); // FIXED: Was 0.7
        flowerArch.setDepth(-100); // ADDED: Behind player
        this.backgroundElements.push(flowerArch);
        break;
    }
  }

  // === LEVEL 3: PARTY THEME ===
  
  createPartyBackground() {
    const { width, height } = this.scene.scale;
    
    // Night sky with stars
    this.createStarryNight();
    
    // Death Star in the sky
    this.createDeathStar();
    
    // Party lights in the sky
    this.createPartyLights();
    
    // Fireworks
    this.createFireworks();
  }

  createStarryNight() {
    const { width, height } = this.scene.scale;
    
    for (let i = 0; i < 50; i++) {
      const star = this.scene.add.circle(
        Math.random() * width * 3,
        Math.random() * height * 0.6,
        1 + Math.random() * 2,
        0xFFFFFF
      );
      
      star.setScrollFactor(0.1);
      star.setAlpha(0.6 + Math.random() * 0.4);
      
      // Twinkling stars
      this.scene.tweens.add({
        targets: star,
        alpha: 0.2,
        duration: 1000 + Math.random() * 2000,
        repeat: -1,
        yoyo: true,
        ease: 'Sine.easeInOut'
      });
      
      this.backgroundElements.push(star);
    }
  }

  createBarWithBand(x, groundLevel) {
    // Bar structure
    const bar = this.scene.add.rectangle(x, groundLevel - 40, 120, 80, 0x8B4513);
    bar.setScrollFactor(0.4);
    bar.setDepth(-90);
    bar.setStrokeStyle(4, 0x654321);
    
    // Bar top
    const barTop = this.scene.add.rectangle(x, groundLevel - 75, 130, 10, 0x654321);
    barTop.setScrollFactor(0.4);
    barTop.setDepth(-89);
    
    // Bar sign
    const barSign = this.scene.add.text(x, groundLevel - 65, 'WEDDING BAR', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#FFD700',
      fontWeight: 'bold'
    }).setOrigin(0.5);
    barSign.setScrollFactor(0.4);
    barSign.setDepth(-88);
    
    // Band members
    const bandPositions = [
      { x: x - 40, instrument: 'GUITAR', color: 0xFF1493 },
      { x: x - 10, instrument: 'DRUMS', color: 0x32CD32 },
      { x: x + 20, instrument: 'BASS', color: 0x4169E1 },
      { x: x + 50, instrument: 'SINGER', color: 0xFFD700 }
    ];
    
    bandPositions.forEach((member, i) => {
      // Band member
      const musician = this.scene.add.rectangle(
        member.x, 
        groundLevel - 25, 
        12, 
        30, 
        0xFFDBB5
      );
      musician.setScrollFactor(0.4);
      musician.setDepth(-87);
      
      // Instrument/mic
      const instrument = this.scene.add.rectangle(
        member.x, 
        groundLevel - 35, 
        8, 
        10, 
        member.color
      );
      instrument.setScrollFactor(0.4);
      instrument.setDepth(-86);
      
      // Simple animation for band
      this.scene.tweens.add({
        targets: [musician, instrument],
        y: groundLevel - 27,
        duration: 800 + (i * 100),
        repeat: -1,
        yoyo: true,
        ease: 'Sine.easeInOut'
      });
      
      this.backgroundElements.push(musician, instrument);
    });
    
    // Stage lights
    for (let i = 0; i < 3; i++) {
      const light = this.scene.add.circle(
        x - 30 + (i * 30), 
        groundLevel - 90, 
        5, 
        Phaser.Utils.Array.GetRandom([0xFF1493, 0x00CED1, 0xFFD700])
      );
      light.setScrollFactor(0.4);
      light.setDepth(-85);
      
      // Pulsing stage lights
      this.scene.tweens.add({
        targets: light,
        alpha: 0.3,
        scaleX: 1.5,
        scaleY: 1.5,
        duration: 600 + (i * 200),
        repeat: -1,
        yoyo: true
      });
      
      this.backgroundElements.push(light);
    }
    
    this.backgroundElements.push(bar, barTop, barSign);
  }

  createDeathStar() {
    const { width, height } = this.scene.scale;
  
    this.createDeathStarSprite();
    
    const deathStar = this.scene.add.image(
      width * 0.5, // FIXED: Was width * 1.5, now appears earlier
      height * 0.15,
      'death-star'
    );
    
    deathStar.setScale(8);
    deathStar.setScrollFactor(0.05); // FIXED: Slightly faster than before
    deathStar.setDepth(-800);
    deathStar.setAlpha(0.8);
    
    // Slow rotation
    this.scene.tweens.add({
      targets: deathStar,
      rotation: Math.PI * 2,
      duration: 60000, // 1 minute rotation
      repeat: -1,
      ease: 'Linear'
    });
    
    // Add Death Star laser effect occasionally
    this.scene.time.addEvent({
      delay: 15000,
      callback: () => {
        if (deathStar.active) {
          this.createDeathStarLaser(deathStar.x, deathStar.y);
        }
      },
      loop: true
    });
    
    this.backgroundElements.push(deathStar);
  }

  createPartyLights() {
    const { width, height } = this.scene.scale;
    
    // Colorful party lights across the sky
    for (let i = 0; i < 20; i++) {
      const light = this.scene.add.circle(
        Math.random() * width * 2,
        height * 0.05 + Math.random() * (height * 0.3),
        3 + Math.random() * 2,
        Phaser.Utils.Array.GetRandom([0xFF1493, 0x00CED1, 0xFFD700, 0x32CD32, 0xFF6347])
      );
      
      light.setScrollFactor(0.3);
      
      // Pulsing party lights
      this.scene.tweens.add({
        targets: light,
        scaleX: 1.5,
        scaleY: 1.5,
        duration: 500 + Math.random() * 1000,
        repeat: -1,
        yoyo: true,
        ease: 'Sine.easeInOut'
      });
      
      this.backgroundElements.push(light);
    }
  }

  createFireworks() {
    const { width, height } = this.scene.scale;
    
    // Occasional fireworks in the background
    this.scene.time.addEvent({
      delay: 5000,
      callback: () => {
        const fireworkX = Math.random() * width * 1.5;
        const fireworkY = height * 0.2 + Math.random() * (height * 0.2);
        
        // Firework explosion
        for (let i = 0; i < 12; i++) {
          const particle = this.scene.add.circle(
            fireworkX,
            fireworkY,
            2,
            Phaser.Utils.Array.GetRandom([0xFF1493, 0x00CED1, 0xFFD700, 0xFF6347, 0x32CD32])
          );
          
          particle.setScrollFactor(0.4);
          
          const angle = (i / 12) * Math.PI * 2;
          const speed = 30 + Math.random() * 20;
          
          this.scene.tweens.add({
            targets: particle,
            x: fireworkX + Math.cos(angle) * speed,
            y: fireworkY + Math.sin(angle) * speed,
            alpha: 0,
            duration: 1500,
            ease: 'Quad.easeOut',
            onComplete: () => {
              if (particle.active) particle.destroy();
            }
          });
        }
      },
      loop: true
    });
  }

  createPartyDecorations() {
    const { width, height } = this.scene.scale;
    
    // Create party sprites
    this.createPartySprites();
    
    // Main club building
    const clubX = width * 1.2;
    this.createNightclub(clubX, height);
    
    // Fire bowls and party decorations
    for (let i = 0; i < 10; i++) {
      const x = width + (i * 300) + 100;
      this.createPartyDecoration(x, height, i);
    }
  }

  createNightclub(x, height) {
    const groundLevel = height - 30;
    
    // Main club building
    const club = this.scene.add.image(x, groundLevel - 100, 'nightclub');
    club.setScale(8, 6);
    club.setScrollFactor(0.2); // FIXED: Was 0.5
    club.setDepth(-400); // ADDED: Explicit depth
    
    // Disco ball on top
    const discoBall = this.scene.add.image(x, groundLevel - 180, 'disco-ball');
    discoBall.setScale(4);
    discoBall.setScrollFactor(0.2); // FIXED: Was 0.5
    discoBall.setDepth(-399); // ADDED: Explicit depth
    
    // Disco ball rotation and sparkles
    this.scene.tweens.add({
      targets: discoBall,
      rotation: Math.PI * 2,
      duration: 3000,
      repeat: -1,
      ease: 'Linear'
    });
    
    // Club entrance lighting
    const entranceLights = this.scene.add.rectangle(x, groundLevel - 50, 100, 80, 0xFF1493, 0.3);
    entranceLights.setScrollFactor(0.2); // FIXED: Was 0.5
    entranceLights.setDepth(-398); // ADDED: Explicit depth
    
    // Pulsing entrance lights
    this.scene.tweens.add({
      targets: entranceLights,
      alpha: 0.1,
      duration: 800,
      repeat: -1,
      yoyo: true,
      ease: 'Sine.easeInOut'
    });
    
    // Club sign
    const clubSign = this.scene.add.text(
      x,
      groundLevel - 150,
      'PABLO & BETTINA\'S\nWEDDING PARTY',
      {
        fontSize: '16px',
        fontFamily: 'Arial, sans-serif',
        color: '#FFD700',
        fontWeight: 'bold',
        align: 'center',
        stroke: '#FF1493',
        strokeThickness: 2
      }
    ).setOrigin(0.5);
    clubSign.setScrollFactor(0.2); // FIXED: Was 0.5
    clubSign.setDepth(-397); // ADDED: Explicit depth
    
    this.backgroundElements.push(club, discoBall, entranceLights, clubSign);
  }

  createPartyDecoration(x, height, index) {
  const groundLevel = height - 30;
  
  if (index % 4 === 0) {
    // Fire bowls
    const fireBowl = this.scene.add.image(x, groundLevel - 15, 'fire-bowl');
    fireBowl.setScale(2.5);
    fireBowl.setScrollFactor(0.4); // FIXED: Was 0.7, now behind player
    fireBowl.setDepth(-80); // ADDED: Behind player
    
    const fire = this.scene.add.image(x, groundLevel - 25, 'fire-effect');
    fire.setScale(2);
    fire.setScrollFactor(0.4); // FIXED: Was 0.7
    fire.setDepth(-79); // ADDED: Behind player
    fire.setTint(0xFF4500);
    
    const glow = this.scene.add.circle(x, groundLevel - 25, 30, 0xFF4500, 0.2);
    glow.setScrollFactor(0.4); // FIXED: Was 0.7
    glow.setDepth(-81); // ADDED: Behind player
    
    this.backgroundElements.push(fireBowl, fire, glow);
    
  } else if (index % 4 === 1) {
    // DJ booth/speakers
    const speaker = this.scene.add.image(x, groundLevel - 30, 'speaker');
    speaker.setScale(3);
    speaker.setScrollFactor(0.4); // FIXED: Was 0.7
    speaker.setDepth(-80); // ADDED: Behind player
    
    this.backgroundElements.push(speaker);
    
  } else if (index % 4 === 2) {
    // Party spotlights
    const spotlight = this.scene.add.image(x, groundLevel - 80, 'spotlight');
    spotlight.setScale(2);
    spotlight.setScrollFactor(0.4); // FIXED: Was 0.7
    spotlight.setDepth(-80); // ADDED: Behind player
    
    const beam = this.scene.add.rectangle(x, groundLevel - 40, 20, 80, 0xFFFFFF, 0.3);
    beam.setScrollFactor(0.4); // FIXED: Was 0.7
    beam.setDepth(-79); // ADDED: Behind player
    
    this.backgroundElements.push(spotlight, beam);
    
  } else {
    // NEW: Bar with band
    this.createBarWithBand(x, groundLevel);
  }
}

  // === SPRITE CREATION METHODS ===
  
  createPlaneSprite() {
    if (this.scene.textures.exists('plane')) return;
    
    const graphics = this.scene.add.graphics();
    
    // Plane body
    graphics.fillStyle(0xC0C0C0);
    graphics.fillRect(0, 4, 20, 4); // Fuselage
    
    // Wings
    graphics.fillStyle(0xA0A0A0);
    graphics.fillRect(6, 2, 8, 8); // Main wings
    graphics.fillRect(14, 4, 4, 4); // Tail
    
    // Propeller
    graphics.fillStyle(0x8B4513);
    graphics.fillRect(20, 5, 3, 2);
    
    graphics.generateTexture('plane', 24, 12);
    graphics.destroy();
  }

  createBalloonSprite() {
    if (this.scene.textures.exists('balloon')) return;
    
    const graphics = this.scene.add.graphics();
    
    // Balloon
    graphics.fillStyle(0xFF69B4);
    graphics.fillCircle(8, 8, 8);
    
    // Balloon highlight
    graphics.fillStyle(0xFFB6C1);
    graphics.fillCircle(6, 6, 3);
    
    // String
    graphics.fillStyle(0x8B4513);
    graphics.fillRect(8, 16, 1, 8);
    
    graphics.generateTexture('balloon', 16, 24);
    graphics.destroy();
  }

  createDoveSprite() {
    if (this.scene.textures.exists('dove')) return;
    
    const graphics = this.scene.add.graphics();
    
    // Dove body
    graphics.fillStyle(0xFFFFFF);
    graphics.fillRect(4, 6, 8, 4); // Body
    graphics.fillRect(2, 4, 4, 2); // Head
    
    // Wings
    graphics.fillStyle(0xF0F0F0);
    graphics.fillRect(1, 5, 6, 3); // Left wing
    graphics.fillRect(9, 5, 6, 3); // Right wing
    
    // Beak
    graphics.fillStyle(0xFFD700);
    graphics.fillRect(1, 5, 2, 1);
    
    graphics.generateTexture('dove', 16, 12);
    graphics.destroy();
  }

  createPetalSprite() {
    if (this.scene.textures.exists('petal')) return;
    
    const graphics = this.scene.add.graphics();
    
    // Petal shape
    graphics.fillStyle(0xFF69B4);
    graphics.fillCircle(4, 2, 3); // Top
    graphics.fillCircle(2, 6, 3); // Bottom left
    graphics.fillCircle(6, 6, 3); // Bottom right
    
    graphics.generateTexture('petal', 8, 8);
    graphics.destroy();
  }

  createPavilionSprites() {
    if (this.scene.textures.exists('pavilion')) return;
    
    // Pavilion
    const graphics = this.scene.add.graphics();
    graphics.fillStyle(0xFFFEF7);
    graphics.fillRect(0, 8, 32, 16); // Base
    graphics.fillRect(4, 0, 24, 12); // Roof
    graphics.fillStyle(0xF0F0F0);
    graphics.fillRect(0, 20, 32, 4); // Shadow
    graphics.generateTexture('pavilion', 32, 24);
    graphics.destroy();
    
    // Additional pavilion sprites...
    const graphics2 = this.scene.add.graphics();
    graphics2.fillStyle(0xF8F8FF);
    for (let i = 0; i < 8; i++) {
      graphics2.fillRect(i * 4, 0, 3, 16);
    }
    graphics2.generateTexture('draping', 32, 16);
    graphics2.destroy();
    
    // Wedding arch
    const graphics3 = this.scene.add.graphics();
    graphics3.fillStyle(0xFFFFFF);
    graphics3.fillRect(2, 8, 4, 16); // Left pillar
    graphics3.fillRect(26, 8, 4, 16); // Right pillar
    graphics3.fillRect(6, 8, 20, 4); // Top arch
    // Flowers on arch
    graphics3.fillStyle(0xFF69B4);
    for (let i = 0; i < 5; i++) {
      graphics3.fillCircle(8 + (i * 4), 6, 2);
    }
    graphics3.generateTexture('wedding-arch', 32, 24);
    graphics3.destroy();
    
    // Flower stand
    const graphics4 = this.scene.add.graphics();
    graphics4.fillStyle(0x8B4513);
    graphics4.fillRect(6, 12, 4, 8); // Stand
    graphics4.fillStyle(0xFF69B4);
    graphics4.fillCircle(8, 8, 6); // Flowers
    graphics4.fillStyle(0xFFFFFF);
    graphics4.fillCircle(6, 6, 3);
    graphics4.fillCircle(10, 6, 3);
    graphics4.fillStyle(0x32CD32);
    graphics4.fillRect(8, 14, 2, 6); // Stems
    graphics4.generateTexture('flower-stand', 16, 20);
    graphics4.destroy();
    
    // Ceremonial candle
    const graphics5 = this.scene.add.graphics();
    graphics5.fillStyle(0xFFFEF7);
    graphics5.fillRect(6, 4, 4, 16); // Candle
    graphics5.fillStyle(0xFFD700);
    graphics5.fillRect(7, 2, 2, 3); // Flame
    graphics5.fillStyle(0x8B4513);
    graphics5.fillRect(4, 18, 8, 2); // Base
    graphics5.generateTexture('ceremonial-candle', 16, 20);
    graphics5.destroy();
    
    // Wedding chair
    const graphics6 = this.scene.add.graphics();
    graphics6.fillStyle(0xFFFFFF);
    graphics6.fillRect(2, 8, 8, 8); // Seat
    graphics6.fillRect(2, 4, 8, 4); // Back
    graphics6.fillRect(2, 14, 2, 4); // Legs
    graphics6.fillRect(8, 14, 2, 4);
    graphics6.generateTexture('wedding-chair', 12, 18);
    graphics6.destroy();
    
    // Flower arch
    const graphics7 = this.scene.add.graphics();
    graphics7.fillStyle(0x32CD32);
    graphics7.fillRect(0, 12, 24, 4); // Arch base
    graphics7.fillStyle(0xFF69B4);
    for (let i = 0; i < 6; i++) {
      graphics7.fillCircle(2 + (i * 4), 10, 3);
    }
    graphics7.fillStyle(0xFFFFFF);
    for (let i = 0; i < 5; i++) {
      graphics7.fillCircle(4 + (i * 4), 8, 2);
    }
    graphics7.generateTexture('flower-arch', 24, 16);
    graphics7.destroy();
    
    // Lantern
    const graphics8 = this.scene.add.graphics();
    graphics8.fillStyle(0xFFD700);
    graphics8.fillRect(4, 4, 8, 12); // Lantern body
    graphics8.fillStyle(0xFFF8DC);
    graphics8.fillRect(6, 6, 4, 8); // Light
    graphics8.fillStyle(0x8B4513);
    graphics8.fillRect(7, 2, 2, 2); // Top
    graphics8.generateTexture('lantern', 16, 18);
    graphics8.destroy();
  }

  createDeathStarSprite() {
    if (this.scene.textures.exists('death-star')) return;
    
    const graphics = this.scene.add.graphics();
    
    // Death Star sphere
    graphics.fillStyle(0x696969);
    graphics.fillCircle(16, 16, 16);
    
    // Surface details
    graphics.fillStyle(0x2F2F2F);
    graphics.fillCircle(16, 16, 14);
    
    // Superlaser dish
    graphics.fillStyle(0x1C1C1C);
    graphics.fillCircle(12, 10, 4);
    
    // Surface panels
    graphics.fillStyle(0x404040);
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const x = 16 + Math.cos(angle) * 10;
      const y = 16 + Math.sin(angle) * 10;
      graphics.fillRect(x, y, 3, 3);
    }
    
    graphics.generateTexture('death-star', 32, 32);
    graphics.destroy();
  }

  createPartySprites() {
    if (this.scene.textures.exists('nightclub')) return;
    
    // Nightclub
    const graphics = this.scene.add.graphics();
    graphics.fillStyle(0x2C3E50);
    graphics.fillRect(0, 8, 32, 24); // Building
    graphics.fillStyle(0xFF1493);
    graphics.fillRect(4, 12, 24, 16); // Entrance
    graphics.fillStyle(0xFFD700);
    graphics.fillRect(8, 16, 16, 8); // Windows
    graphics.generateTexture('nightclub', 32, 32);
    graphics.destroy();
    
    // Fire bowl
    const graphics2 = this.scene.add.graphics();
    graphics2.fillStyle(0x8B4513);
    graphics2.fillRect(4, 12, 8, 4); // Bowl
    graphics2.fillStyle(0xFF4500);
    graphics2.fillRect(6, 8, 4, 4); // Fire base
    graphics2.generateTexture('fire-bowl', 16, 16);
    graphics2.destroy();
    
    // Fire effect
    const graphics3 = this.scene.add.graphics();
    graphics3.fillStyle(0xFF4500);
    graphics3.fillRect(2, 4, 4, 8); // Flame 1
    graphics3.fillRect(6, 2, 4, 10); // Flame 2
    graphics3.fillRect(10, 4, 4, 8); // Flame 3
    graphics3.fillStyle(0xFFD700);
    graphics3.fillRect(4, 6, 8, 4); // Inner flame
    graphics3.generateTexture('fire-effect', 16, 12);
    graphics3.destroy();
    
    // Speaker
    const graphics4 = this.scene.add.graphics();
    graphics4.fillStyle(0x1C1C1C);
    graphics4.fillRect(0, 0, 16, 20); // Speaker body
    graphics4.fillStyle(0x2F2F2F);
    graphics4.fillCircle(8, 6, 4); // Tweeter
    graphics4.fillCircle(8, 14, 6); // Woofer
    graphics4.fillStyle(0x696969);
    graphics4.fillCircle(8, 6, 2);
    graphics4.fillCircle(8, 14, 4);
    graphics4.generateTexture('speaker', 16, 20);
    graphics4.destroy();
    
    // Spotlight
    const graphics5 = this.scene.add.graphics();
    graphics5.fillStyle(0x2F2F2F);
    graphics5.fillRect(4, 8, 8, 8); // Light housing
    graphics5.fillStyle(0xFFFFFF);
    graphics5.fillCircle(8, 12, 3); // Light
    graphics5.fillStyle(0x8B4513);
    graphics5.fillRect(7, 16, 2, 4); // Stand
    graphics5.generateTexture('spotlight', 16, 20);
    graphics5.destroy();
    
    // Disco ball
    const graphics6 = this.scene.add.graphics();
    graphics6.fillStyle(0xC0C0C0);
    graphics6.fillCircle(8, 8, 8); // Ball
    // Mirror squares
    graphics6.fillStyle(0xFFFFFF);
    for (let x = 0; x < 4; x++) {
      for (let y = 0; y < 4; y++) {
        if ((x + y) % 2 === 0) {
          graphics6.fillRect(2 + x * 3, 2 + y * 3, 2, 2);
        }
      }
    }
    graphics6.generateTexture('disco-ball', 16, 16);
    graphics6.destroy();
  }

  createShopSprites() {
    if (this.scene.textures.exists('big-shop')) return;
    
    // Big shop/building
    const graphics = this.scene.add.graphics();
    graphics.fillStyle(0xD2B48C);
    graphics.fillRect(0, 0, 32, 32); // Building
    graphics.fillStyle(0x8B4513);
    graphics.fillRect(0, 28, 32, 4); // Base
    graphics.fillStyle(0x4169E1);
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        graphics.fillRect(4 + (i * 8), 4 + (j * 6), 6, 4); // Windows
      }
    }
    graphics.generateTexture('big-shop', 32, 32);
    graphics.destroy();
    
    // Small shop
    const graphics2 = this.scene.add.graphics();
    graphics2.fillStyle(0xF5DEB3);
    graphics2.fillRect(0, 8, 24, 16); // Shop
    graphics2.fillStyle(0xFF69B4);
    graphics2.fillRect(0, 8, 24, 4); // Awning
    graphics2.fillStyle(0x87CEEB);
    graphics2.fillRect(6, 12, 12, 8); // Window
    graphics2.fillStyle(0x8B4513);
    graphics2.fillRect(18, 16, 6, 8); // Door
    graphics2.generateTexture('small-shop', 24, 24);
    graphics2.destroy();
  }

  createBirds() {
    const { width, height } = this.scene.scale;
    
    // Create bird sprite
    if (!this.scene.textures.exists('bird')) {
      const graphics = this.scene.add.graphics();
      graphics.fillStyle(0x2F4F4F);
      graphics.fillRect(2, 2, 6, 2); // Body
      graphics.fillRect(0, 1, 4, 1); // Left wing
      graphics.fillRect(6, 1, 4, 1); // Right wing
      graphics.generateTexture('bird', 10, 4);
      graphics.destroy();
    }
    
    for (let i = 0; i < 6; i++) {
      const bird = this.scene.add.image(
        Math.random() * width * 1.5,
        height * 0.15 + Math.random() * (height * 0.2),
        'bird'
      );
      
      bird.setScale(2);
      bird.setScrollFactor(0.4);
      
      // Bird flying
      this.scene.tweens.add({
        targets: bird,
        x: bird.x + 300,
        y: bird.y + (Math.random() - 0.5) * 50,
        duration: 8000 + Math.random() * 4000,
        repeat: -1,
        yoyo: true,
        ease: 'Sine.easeInOut'
      });
      
      // Wing flapping
      this.scene.tweens.add({
        targets: bird,
        scaleY: 1.5,
        duration: 200,
        repeat: -1,
        yoyo: true
      });
      
      this.backgroundElements.push(bird);
    }
  }

  createDeathStarLaser(x, y) {
    const { width, height } = this.scene.scale;
    
    // Laser beam effect
    const laser = this.scene.add.rectangle(
      x,
      y,
      5,
      height * 2,
      0x00FF00,
      0.8
    );
    
    laser.setScrollFactor(0.05);
    laser.setOrigin(0.5, 0);
    
    // Laser charging effect
    this.scene.tweens.add({
      targets: laser,
      scaleX: 0.2,
      alpha: 1,
      duration: 500,
      yoyo: true,
      onComplete: () => {
        if (laser.active) laser.destroy();
      }
    });
    
    // Screen flash
    this.scene.cameras.main.flash(200, 0, 255, 0, false);
  }

  createGround() {
    const { width, height } = this.scene.scale;
    const physicsGroups = this.scene.getPhysicsGroups();
    
    // Create ground based on level theme
    let groundColor;
    switch (gameState.level) {
      case 1: // Shopping - make ground darker so texture shows
        groundColor = 0x808080; // DARKER gray so brown bricks show
        break;
      case 2: // Ceremony - make ground darker so grass shows
        groundColor = 0x1F4F1F; // DARKER green so light grass shows
        break;
      case 3: // Party - keep dark for contrast
        groundColor = 0x1C1C1C; // DARKER for wood to show
        break;
    }
    
    // Ground sprite
    const ground = this.scene.add.rectangle(
      width * 10, 
      height - 15, 
      width * 20, 
      30, 
      groundColor
    );
    ground.setDepth(-20); // MOVED GROUND FURTHER BACK
    
    // Add ground physics
    this.scene.physics.add.existing(ground, true);
    physicsGroups.ground.add(ground);
    
    // Create texture AFTER ground is created
    this.addGroundTexture(ground, gameState.level);
    
    /* console.log(`🌍 Level ${gameState.level} ground created with texture`); */
  }


  addGroundTexture(ground, level) {
    const { width, height } = this.scene.scale;
    
    /* console.log(`🎨 Creating VISIBLE ground texture for level ${level}`); */
    
    switch (level) {
      case 1: // BRICK TEXTURE - MUCH SIMPLER AND VISIBLE
        // Create simple brick lines that are clearly visible
        for (let i = 0; i < 100; i++) {
          // Horizontal brick lines
          const brickLine = this.scene.add.rectangle(
            i * 20,
            height - 10, // ON TOP of ground
            18,
            3,
            0xD2B48C // LIGHT tan color (visible on dark gray)
          );
          brickLine.setScrollFactor(1.0);
          brickLine.setDepth(5); // IN FRONT of everything
          this.backgroundElements.push(brickLine);
          
          // Vertical mortar lines every 40px
          if (i % 2 === 0) {
            const mortarLine = this.scene.add.rectangle(
              i * 20,
              height - 15,
              1,
              30,
              0xF5DEB3 // LIGHT beige mortar
            );
            mortarLine.setScrollFactor(1.0);
            mortarLine.setDepth(5);
            this.backgroundElements.push(mortarLine);
          }
        }
        /* console.log('✅ Simple brick texture created ON TOP'); */
        break;
        
      case 2: // GRASS TEXTURE - SIMPLE AND VISIBLE
        // Create grass blades ON TOP of ground
        for (let i = 0; i < 200; i++) {
          const grassBlade = this.scene.add.rectangle(
            Math.random() * width * 20,
            height - 5, // ON TOP of ground surface
            2,
            8,
            0x90EE90 // LIGHT green (visible on dark green ground)
          );
          grassBlade.setScrollFactor(1.0);
          grassBlade.setDepth(5); // IN FRONT
          this.backgroundElements.push(grassBlade);
        }
        
        // Add some flowers
        for (let i = 0; i < 50; i++) {
          const flower = this.scene.add.circle(
            Math.random() * width * 20,
            height - 8,
            2,
            Phaser.Utils.Array.GetRandom([0xFF69B4, 0xFFFFFF, 0xFFD700])
          );
          flower.setScrollFactor(1.0);
          flower.setDepth(6);
          this.backgroundElements.push(flower);
        }
        /* console.log('✅ Simple grass texture created ON TOP'); */
        break;
        
      case 3: // PARQUET TEXTURE - SIMPLE AND VISIBLE
        // Create wood plank lines
        for (let i = 0; i < 100; i++) {
          // Wood planks
          const plank = this.scene.add.rectangle(
            i * 30,
            height - 10,
            25,
            4,
            0x8B4513 // BROWN wood (visible on black floor)
          );
          plank.setScrollFactor(1.0);
          plank.setDepth(5);
          this.backgroundElements.push(plank);
          
          // Wood grain lines
          const grain = this.scene.add.rectangle(
            i * 30 + 12,
            height - 15,
            1,
            30,
            0xA0522D // LIGHTER brown grain
          );
          grain.setScrollFactor(1.0);
          grain.setDepth(5);
          this.backgroundElements.push(grain);
        }
        /* console.log('✅ Simple parquet texture created ON TOP'); */
        break;
    }
  }

  startGroundTextureSpawning() {
    this.lastGroundX = 2000; // Start spawning after initial tiles
    this.groundSpawnDistance = 160; // Spawn every 4 tiles (4 * 40px)
    
    /* console.log('🌍 Starting continuous ground texture spawning'); */
    
    this.scene.time.addEvent({
      delay: 200,
      callback: () => {
        const player = this.scene.getPlayer();
        if (!player) return;
        
        const { width } = this.scene.scale;
        
        // Spawn ground texture ahead of player
        while (this.lastGroundX < player.x + (width * 2)) {
          this.spawnGroundTexture(this.lastGroundX);
          this.lastGroundX += this.groundSpawnDistance;
        }
      },
      loop: true
    });
  }

  spawnGroundTexture(x) {
    const { height } = this.scene.scale;
    
    switch (gameState.level) {
      case 1: // BRICK spawning
        for (let i = 0; i < 8; i++) { // 8 bricks per spawn
          const brickLine = this.scene.add.rectangle(
            x + (i * 20),
            height - 10,
            18,
            3,
            0xD2B48C // Light tan
          );
          brickLine.setScrollFactor(1.0);
          brickLine.setDepth(5);
          this.backgroundElements.push(brickLine);
          
          if (i % 2 === 0) {
            const mortarLine = this.scene.add.rectangle(
              x + (i * 20),
              height - 15,
              1,
              30,
              0xF5DEB3
            );
            mortarLine.setScrollFactor(1.0);
            mortarLine.setDepth(5);
            this.backgroundElements.push(mortarLine);
          }
        }
        break;
        
      case 2: // GRASS spawning
        for (let i = 0; i < 15; i++) {
          const grassBlade = this.scene.add.rectangle(
            x + Math.random() * 160,
            height - 5,
            2,
            8,
            0x90EE90
          );
          grassBlade.setScrollFactor(1.0);
          grassBlade.setDepth(5);
          this.backgroundElements.push(grassBlade);
        }
        
        // Occasional flower
        if (Math.random() > 0.8) {
          const flower = this.scene.add.circle(
            x + Math.random() * 160,
            height - 8,
            2,
            Phaser.Utils.Array.GetRandom([0xFF69B4, 0xFFFFFF, 0xFFD700])
          );
          flower.setScrollFactor(1.0);
          flower.setDepth(6);
          this.backgroundElements.push(flower);
        }
        break;
        
      case 3: // PARQUET spawning
        for (let i = 0; i < 5; i++) {
          const plank = this.scene.add.rectangle(
            x + (i * 30),
            height - 10,
            25,
            4,
            0x8B4513
          );
          plank.setScrollFactor(1.0);
          plank.setDepth(5);
          this.backgroundElements.push(plank);
          
          const grain = this.scene.add.rectangle(
            x + (i * 30) + 12,
            height - 15,
            1,
            30,
            0xA0522D
          );
          grain.setScrollFactor(1.0);
          grain.setDepth(5);
          this.backgroundElements.push(grain);
        }
        break;
    }
  }


  // === CLEANUP METHODS ===
  
  cleanup() {
    this.backgroundElements.forEach(element => {
      if (element && element.active) {
        element.destroy();
      }
    });
    
    this.animatedElements.forEach(element => {
      if (element && element.active) {
        element.destroy();
      }
    });
    
    this.backgroundElements = [];
    this.animatedElements = [];
  }

  // === UTILITY METHODS ===
  
  updateParallax(camera) {
    // Additional parallax effects can be added here if needed
    // The scroll factors are already set on individual elements
  }

  getBackgroundElements() {
    return this.backgroundElements;
  }

  getAnimatedElements() {
    return this.animatedElements;
  }
}

